import { useState, useEffect, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useAuth } from '../../context/AuthContext';
import axiosInstance from "../../api/axiosInstance";
import { CircularProgress } from "@mui/material";
import BottomSheet from '../BottomSheet';
import OpenMediaViewer from './OpenMediaViewer';
import { motion } from "framer-motion";
import { ChevronLeft, Trash2, User, Image, Users, UserPlus, LogOut, ChevronRight } from 'lucide-react';

export default function ChatSettings() {
  const { userId } = useAuth();

  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { chatInfo: initialChatInfo } = location.state || {};

  const [media, setMedia] = useState([]);
  const [mediaPage, setMediaPage] = useState({ limit: 30, offset: 0, hasMore: true });
  const [loadingMedia, setLoadingMedia] = useState(false);

  const [chatInfo, setChatInfo] = useState(initialChatInfo);
  const [bottomSheetOption, setBottomSheetOption] = useState(null);
  const [groupName, setGroupName] = useState(chatInfo?.chat_name || "");

  const loaderRef = useRef(null);
  const [openMedia, setOpenMedia] = useState(null);
  
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNewUser, setSelectedNewUser] = useState(null);

  useEffect(() => {
    if (!loaderRef.current || !mediaPage.hasMore || loadingMedia) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchMedia();
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(loaderRef.current);

    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [loaderRef, mediaPage.hasMore, loadingMedia]);

  useEffect(() => {
    if (!initialChatInfo) {
      navigate(-1);
    }
  }, [initialChatInfo, navigate]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const res = await axiosInstance.get('/general/all_users');
        const existingUserIds = new Set(chatInfo.members.map(m => m.user_id));
        const filteredUsers = res.data.users.filter(u => !existingUserIds.has(u.user_id));
        setAllUsers(filteredUsers);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };

    if (bottomSheetOption === "add_members") {
      fetchAllUsers();
      setSearchTerm("");
      setSelectedNewUser(null);
    }
  }, [bottomSheetOption, chatInfo]);

  const updateGroupName = async (newName) => {
    if (newName.trim() !== chatInfo.chat_name) {
      try {
        await axiosInstance.put(`/chat/update-name/${chatId}`, { name: newName.trim() });
        setChatInfo({ ...chatInfo, chat_name: newName });
      } catch (err) {
        console.error("Failed to update group name:", err);
      } finally {
        setBottomSheetOption(null);
      }
    }
  };

  const handleAddMember = async () => {
    if (!selectedNewUser) return;
    try {
      await axiosInstance.post(`/chat/add_member/${chatId}`, { user_id: selectedNewUser.user_id });
      setChatInfo({
        ...chatInfo,
        members: [...chatInfo.members, selectedNewUser],
      });
    } catch (err) {
      console.error("Failed to add member:", err);
    } finally {
      setBottomSheetOption(null)
    }
  };

  const fetchMedia = async (reset = false) => {
    if (loadingMedia || (!reset && !mediaPage.hasMore)) return;

    setLoadingMedia(true);
    try {
      const res = await axiosInstance.get(`/chat/${chatId}/media`, {
        params: {
          limit: mediaPage.limit,
          offset: reset ? 0 : mediaPage.offset,
        },
      });

      const newMedia = res.data.media;
      //await new Promise(resolve => setTimeout(resolve, 1500));
      setMedia(reset ? newMedia : [...media, ...newMedia]);

      setMediaPage({
        ...mediaPage,
        offset: reset ? newMedia.length : mediaPage.offset + newMedia.length,
        hasMore: newMedia.length === mediaPage.limit,
      });
    } catch (err) {
      console.error("Failed to fetch media:", err);
    } finally {
      setLoadingMedia(false);
    }
  };

  useEffect(() => {
    if (bottomSheetOption === "see_media") {
      fetchMedia(true);
    }
  }, [bottomSheetOption]);

  const nonGroupOptions = [
    { id: "see_profile", label: "See profile", icon: <User className="w-5 h-5 text-gray-700 dark:text-gray-300" /> },
    { id: "see_media", label: "See media", icon: <Image className="w-5 h-5 text-gray-700 dark:text-gray-300" /> },
    { id: "delete_chat", label: "Delete chat", icon: <Trash2 className="w-5 h-5 text-red-600" /> },
  ];

  const groupOptions = [
    { id: "change_group_name", label: "Change group chat name", icon: <User className="w-5 h-5 text-gray-700 dark:text-gray-300" /> },
    { id: "see_members", label: "See members", icon: <Users className="w-5 h-5 text-gray-700 dark:text-gray-300" /> },
    { id: "add_members", label: "Add member", icon: <UserPlus className="w-5 h-5 text-gray-700 dark:text-gray-300" /> },
    { id: "see_media", label: "See media", icon: <Image className="w-5 h-5 text-gray-700 dark:text-gray-300" /> },
    { id: "leave_group", label: "Leave the group", icon: <LogOut className="w-5 h-5 text-red-600" /> },
    { id: "delete_chat", label: "Delete chat", icon: <Trash2 className="w-5 h-5 text-red-600" /> },
  ];

  return (
    <motion.div
      className="tailwind flex flex-col h-screen bg-white dark:bg-darkBackground"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-neutral-800 mt-8">
        <button onClick={() => navigate(`/chat/${chatId}`, { state: { from: 'settings' } })}>
          <ChevronLeft className="w-8 h-8 text-black dark:text-white" />
        </button>
        <div className="text-lg font-bold text-gray-900 dark:text-white">Settings</div>
        <div className="w-8 h-8" />
      </div>

      <div className="flex flex-col items-center p-6 border-b dark:border-neutral-800">
        <img
          src={
            chatInfo?.profile_url ||
            (chatInfo?.is_group
              ? "/assets/images/default_group_picture.png"
              : "/assets/images/default_profile.png")
          }
          alt={chatInfo?.chat_name || "Chat"}
          className="w-24 h-24 rounded-full object-cover mb-2"
        />
        <h1 className="text-xl font-bold text-center text-gray-900 dark:text-white">{chatInfo?.chat_name}</h1>
      </div>

      <div className="p-4 space-y-6 flex-1 overflow-y-auto">
        <div className="space-y-3">
          {(chatInfo?.is_group ? groupOptions : nonGroupOptions).map(({ id, label, icon }) => (
            <div
              key={id}
              onClick={async () => {
                if (["see_members", "see_media", "change_group_name", "add_members"].includes(id)) {
                  setBottomSheetOption(id);
                }
                else {
                  if (id === "see_profile") {
                    navigate(`/profile/${chatInfo.members.find(m => m.user_id !== userId).user_id}`);
                  }
                  if (id === "leave_group") {
                    await axiosInstance.post(`/chat/leave/${chatId}`);
                    navigate("/chat/conversations");
                  }
                  if (id === "delete_chat") {
                    await axiosInstance.post(`/chat/clear/${chatId}`);
                    navigate("/chat/conversations");
                  }
                }
              }}
              className="flex items-center justify-between p-4 rounded-xl bg-gray-100 dark:bg-smallDarkCard"
            >
              <div className="flex items-center gap-3">
                {icon}
                <span
                  className={`font-medium ${
                    id === "delete_chat" || id === "leave_group"
                      ? "text-red-600"
                      : "text-gray-800 dark:text-gray-100"
                  }`}
                >
                  {label}
                </span>
              </div>
              {id !== "report_user" && id !== "leave_group" && id !== "delete_chat" && (
                <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              )}
            </div>
          ))}
        </div>

        <BottomSheet isOpen={!!bottomSheetOption} onClose={() => setBottomSheetOption(null)}>
          {bottomSheetOption === "change_group_name" && (
            <>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setBottomSheetOption(null)}
                  className="text-chatBlueColor text-base"
                >
                  Cancel
                </button>
                <h1 className="text-lg font-semibold">Change Group Name</h1>
                <button
                  onClick={() => updateGroupName(groupName)}
                  className={`text-base font-medium text-chatBlueColor`}
                >
                  Save
                </button>
              </div>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full bg-gray-100 dark:bg-smallDarkCard dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg border-none focus:outline-none p-2 mt-5 text-base"
              />
            </>
          )}

          {bottomSheetOption === "see_members" && (
            <>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setBottomSheetOption(null)}
                  className="text-chatBlueColor text-base"
                >
                  Cancel
                </button>
                <h1 className="ext-lg text-lg font-semibold">Members</h1>
                <div className="w-10" />
              </div>

              <div className="divide-y divide-gray-200 dark:divide-neutral-800">
                {chatInfo.members
                  .filter((m) => !m.left_at)
                  .map((m) => (
                    <div key={m.user_id} className="flex items-center gap-3 cursor-pointer py-3 first:pt-0 last:pb-0">
                      <img src={m.profile_url || "/assets/images/default_profile.png"} className="w-10 h-10 rounded-full object-cover" />
                      <span className="text-gray-800 dark:text-gray-100">{m.user_id === userId ? "You" : m.username}</span>
                    </div>
                  ))}
              </div>
            </>
          )}

          {bottomSheetOption === "add_members" && (
            <>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setBottomSheetOption(null)} className="text-chatBlueColor text-base">Cancel</button>
                <h1 className="text-lg font-semibold">Add Member</h1>
                <button
                  onClick={handleAddMember}
                  disabled={!selectedNewUser}
                  className={`text-base font-medium text-chatBlueColor ${!selectedNewUser ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Add
                </button>
              </div>
              <input
                type="text"
                placeholder="Search users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-100 dark:bg-smallDarkCard dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 rounded-lg border-none focus:outline-none p-2 mb-4 text-base"
              />
              <div className="overflow-y-auto">
                {allUsers.filter(u => u.username.toLowerCase().includes(searchTerm.toLowerCase())).map(user => (
                  <div
                    key={user.user_id}
                    onClick={() => setSelectedNewUser(user)}
                    className={`flex items-center gap-3 cursor-pointer py-2 px-3 rounded-lg ${
                      selectedNewUser?.user_id === user.user_id ? "bg-chatBlueColor text-white" : "hover:bg-gray-200 dark:hover:bg-[#2a2a2a]"
                    }`}
                  >
                    <img src={user.profile_url || "/assets/images/default_profile.png"} alt={user.username} className="w-10 h-10 rounded-full object-cover" />
                    <span className="flex-1">{user.username}</span>
                    {selectedNewUser?.user_id === user.user_id && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                ))}
                {allUsers.filter(u => u.username.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-4">No users found</div>
                )}
              </div>
            </>
          )}

          {bottomSheetOption === "see_media" && (
            <>
              <div className="flex items-center justify-between mb-5">
                <button
                  onClick={() => setBottomSheetOption(null)}
                  className="text-chatBlueColor text-base"
                >
                  Cancel
                </button>
                <h1 className="text-lg font-semibold">Media</h1>
                <div className="w-10" />
              </div>

              <div className="grid grid-cols-3 gap-1">
                {media.map((m) => (
                  <div
                    key={m.message_id}
                    onClick={() => setOpenMedia({ type: m.type, src: m.content })}
                    className="relative"
                  >
                    {m.type === "photo" ? (
                      <img src={m.content} className="w-full h-32 object-cover rounded-lg" />
                    ) : (
                      <>
                        <video src={m.content} className="w-full h-32 object-cover rounded-lg" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-14 h-14 text-white drop-shadow-lg"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.35)" />
                            <polygon points="10,8 16,12 10,16" fill="white" />
                          </svg>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {mediaPage.hasMore && (
                <div ref={loaderRef} className="h-10 flex justify-center items-center pt-56">
                  {loadingMedia && (
                    <CircularProgress size={30} sx={{ color: '#d1d5db' }} />
                  )}
                </div>
              )}
            </>
          )}
        </BottomSheet>
      </div>
      
      {openMedia && (
        <OpenMediaViewer openMedia={openMedia} onClose={() => setOpenMedia(null)} />
      )}
    </motion.div>
  );
}